/**
 * Zustand store for Flash Sale data
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getActiveDevices, getDeviceSettings, isVariantEnabled } from "@/stores/deviceSettingsStore";
import { getDeviceVariants } from "@/data/catalog";
import { isStorefrontCategoryEnabled } from "@/stores/swapStore";

const FLASH_SALE_KEY = "khays_flash_sale";

export type FlashSaleAnimation = "pulse" | "glow" | "slide" | "bounce" | "none";
export type FlashSaleColorScheme = "fire" | "ocean" | "gold" | "neon" | "midnight" | "default";
export type FlashSaleDiscountType = "manual" | "percentile" | "formula";
export type FlashSaleRotationUnit = "minutes" | "hours" | "days" | "weeks";
export type FlashSaleCommissionType = "flat" | "percentile";

export interface FlashSale {
  enabled: boolean;
  deviceName: string;
  variantLabel: string;
  specialPrice: number;
  useFormulaPrice: boolean;
  discountType: FlashSaleDiscountType;
  discountPercent: number;
  label: string;
  grade: string;
  screenCondition?: string;
  subVariants?: Record<string, string>;
  animation: FlashSaleAnimation;
  colorScheme: FlashSaleColorScheme;
  whatsappNumber: string;
  maxOrders: number;
  confirmedOrders: number;
  staffCommission: number;
  staffCommissionType: FlashSaleCommissionType;
  staffCommissionPercent: number;
  autoPickEnabled: boolean;
  autoPickCategories: string[];
  autoRotationMinutes: number;
  autoRotationUnit: FlashSaleRotationUnit;
  autoRotationDisplayValue: number;
  lastRotatedAt: number;
  popupEnabled: boolean;
  popupIntervalMinutes: number;
  popupIntervalUnit: FlashSaleRotationUnit;
  popupIntervalDisplayValue: number;
  /** When true, auto-rotation triggers immediately once all orders are claimed
   * instead of waiting for the timer to expire. Defaults to false so the
   * existing "wait for rotation time" behaviour is preserved. */
  rotateOnAllClaimed?: boolean;
}

function loadFromStorage(): FlashSale | null {
  try {
    const stored = localStorage.getItem(FLASH_SALE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed) return {
        animation: "pulse", colorScheme: "default", whatsappNumber: "",
        maxOrders: 0, confirmedOrders: 0, staffCommission: 0,
        screenCondition: undefined, useFormulaPrice: false,
        discountType: parsed.useFormulaPrice ? "formula" : "manual",
        discountPercent: 0, staffCommissionType: "flat", staffCommissionPercent: 0,
        subVariants: undefined, autoPickEnabled: false, autoPickCategories: [],
        autoRotationMinutes: 0, autoRotationUnit: "minutes", autoRotationDisplayValue: 0,
        lastRotatedAt: 0, popupEnabled: false, popupIntervalMinutes: 60,
        popupIntervalUnit: "minutes", popupIntervalDisplayValue: 60,
        ...parsed,
      };
    }
  } catch {}
  return null;
}

interface FlashSaleState {
  sale: FlashSale | null;
  _rev: number;
  setSale: (sale: FlashSale) => void;
  clear: () => void;
  reload: () => void;
}

export const useFlashSaleStore = create<FlashSaleState>()(
  devtools(
    (set, get) => ({
      sale: loadFromStorage(),
      _rev: 0,
      setSale: (sale) => {
        localStorage.setItem(FLASH_SALE_KEY, JSON.stringify(sale));
        set({ sale, _rev: get()._rev + 1 });
      },
      clear: () => {
        localStorage.removeItem(FLASH_SALE_KEY);
        set({ sale: null, _rev: get()._rev + 1 });
      },
      reload: () => set({ sale: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "FlashSaleStore" }
  )
);

// ─── Backward-compatible exports ──────────────────────────────

export function loadFlashSale(): FlashSale | null { return useFlashSaleStore.getState().sale; }

export function saveFlashSale(sale: FlashSale): void { useFlashSaleStore.getState().setSale(sale); }

export function clearFlashSale(): void { useFlashSaleStore.getState().clear(); }

export function unitToMinutes(value: number, unit: FlashSaleRotationUnit): number {
  switch (unit) {
    case "hours": return value * 60;
    case "days": return value * 1440;
    case "weeks": return value * 10080;
    default: return value;
  }
}

export function shouldAutoRotate(sale: FlashSale): boolean {
  if (!sale.autoPickEnabled) return false;
  // Rotate immediately when everything is claimed and the operator opted in.
  if (sale.rotateOnAllClaimed && isFlashSaleAllClaimed(sale)) return true;
  if (sale.autoRotationMinutes <= 0) return false;
  return (Date.now() - (sale.lastRotatedAt || 0)) / 60000 >= sale.autoRotationMinutes;
}

export function getActiveFlashSale(): FlashSale | null {
  const sale = loadFlashSale();
  if (!sale || !sale.enabled) return null;
  // "All claimed" only disables the sale when auto-rotation isn't going to
  // pick a new one. Otherwise the scheduler will rotate on its next tick.
  if (sale.maxOrders > 0 && sale.confirmedOrders >= sale.maxOrders) {
    if (!(sale.autoPickEnabled && sale.rotateOnAllClaimed)) {
      sale.enabled = false;
      saveFlashSale(sale);
      return null;
    }
  }
  // Validate device is enabled and category is visible
  const device = getActiveDevices().find(d => d.name === sale.deviceName);
  if (!device) return null;
  const ds = getDeviceSettings(device.name);
  if (!ds.enabled) return null;
  if (!isStorefrontCategoryEnabled(device.category)) return null;
  const enabledVariants = getDeviceVariants(device).filter((variant) => isVariantEnabled(device.name, variant.label));
  if (enabledVariants.length === 0) return null;
  if (!enabledVariants.some((variant) => variant.label === sale.variantLabel)) return null;
  return sale;
}

export function isFlashSaleAllClaimed(sale: FlashSale): boolean {
  return sale.maxOrders > 0 && sale.confirmedOrders >= sale.maxOrders;
}

export function incrementFlashSaleOrders(): void {
  const sale = loadFlashSale();
  if (!sale) return;
  sale.confirmedOrders = (sale.confirmedOrders || 0) + 1;
  saveFlashSale(sale);
  // If this claim finished the batch and the operator opted in to
  // auto-rotate on all-claimed, kick a rotation right now.
  if (sale.autoPickEnabled && sale.rotateOnAllClaimed && isFlashSaleAllClaimed(sale)) {
    void rotateFlashSaleNow();
  }
}

// ─── Rotation scheduler ────────────────────────────────────────

let _rotationTimer: number | null = null;
let _visibilityHandler: (() => void) | null = null;

/** Import lazily to avoid a circular dep between store and rotation helper. */
async function _pick(sale: FlashSale): Promise<FlashSale> {
  const { pickRotatedFlashSale } = await import("@/lib/flashSaleRotation");
  return pickRotatedFlashSale(sale);
}

/** Force a rotation immediately if the current sale allows it. */
export async function rotateFlashSaleNow(): Promise<void> {
  const sale = loadFlashSale();
  if (!sale || !sale.enabled || !sale.autoPickEnabled) return;
  const next = await _pick(sale);
  if (next !== sale) saveFlashSale(next);
}

/** Called by the ticker every 30s + on tab-visibility change. */
async function _tick(): Promise<void> {
  const sale = loadFlashSale();
  if (!sale || !sale.enabled || !sale.autoPickEnabled) return;
  if (!shouldAutoRotate(sale)) return;
  const next = await _pick(sale);
  if (next !== sale) saveFlashSale(next);
}

/** Boot the rotation scheduler. Safe to call more than once. */
export function startFlashSaleRotationScheduler(): void {
  if (typeof window === "undefined") return;
  if (_rotationTimer !== null) return;
  _rotationTimer = window.setInterval(() => { void _tick(); }, 30_000);
  _visibilityHandler = () => {
    if (document.visibilityState === "visible") void _tick();
  };
  document.addEventListener("visibilitychange", _visibilityHandler);
  // Immediate pass so a stale deal doesn't linger through the first 30s.
  void _tick();
}

/** Stop the scheduler (mostly useful for tests / hot reload). */
export function stopFlashSaleRotationScheduler(): void {
  if (_rotationTimer !== null) { clearInterval(_rotationTimer); _rotationTimer = null; }
  if (_visibilityHandler) { document.removeEventListener("visibilitychange", _visibilityHandler); _visibilityHandler = null; }
}

export const FLASH_SALE_ANIMATIONS: { id: FlashSaleAnimation; label: string }[] = [
  { id: "pulse", label: "Pulse" }, { id: "glow", label: "Glow" },
  { id: "slide", label: "Slide" }, { id: "bounce", label: "Bounce" }, { id: "none", label: "None" },
];

export const FLASH_SALE_COLOR_SCHEMES: { id: FlashSaleColorScheme; label: string; colors: string }[] = [
  { id: "default", label: "Brand Blue", colors: "from-primary via-primary/90 to-primary/80" },
  { id: "fire", label: "Fire Red", colors: "from-red-600 via-orange-500 to-yellow-500" },
  { id: "ocean", label: "Ocean Blue", colors: "from-cyan-600 via-blue-600 to-indigo-600" },
  { id: "gold", label: "Gold Rush", colors: "from-amber-500 via-yellow-500 to-orange-400" },
  { id: "neon", label: "Neon Green", colors: "from-emerald-500 via-green-400 to-lime-400" },
  { id: "midnight", label: "Midnight", colors: "from-slate-800 via-purple-900 to-slate-800" },
];
